import * as React from "react";
import { RouteComponentProps, withRouter } from "react-router";
import { connect, Dispatch } from "react-redux";
import { IUser, ICenter, ICenterMetrics, IBaseResponse } from "@nkr/models/index";
import { IApplicationState, ICenterResponse, AppActionCreators, ICenterMetricsResponse, FileActionCreators, IAppState } from "@nkr/store/index";
import { Badge, Glyphicon, OverlayTrigger } from "react-bootstrap";
import { downloadFile } from '@nkr/services/download-file';
import { toast } from "react-toastify";
import { ChainEndDeclineRatioOverlay } from "./action-center/center-performance/relative-performance-overlays";
import { FileDocumentType, FileDocumentTypeDescription, OrganType, VersionedFileEntityType } from '@nkr/enums/index';
import NavVertical from "./nav-vertical";
import ActionCenter from "./action-center";
import Kpd from "./kpd";
import Billing from "./billing";
import CenterProfileContainer from "@nkr/views/center-profile/center-profile-container";
import Donors from '@nkr/views/donors/index';
import CenterReports from '@nkr/views/reports/center-reports';
import Recipients from "../recipients";
import IncidentReportDetails from '@nkr/views/dash/donor-connect/incident-report/incident-report-details';
import MicrositeContainer from '@nkr/views/microsite/microsite-container';
import DonorsNav from "../donors/tabs";
import FollowUpContainer from '@nkr/views/donors/follow-up';
import Donor from '../donor';
import CenterLegalDocuments from '@nkr/views/center-profile/edit/legal-documents';
import RegulatoryCompliance from './regulatory-compliance';
import NetworkEmailSentList from '@nkr/views/admin/email/sent';

interface IOwnProps extends RouteComponentProps<any> {
    centerId: number,
    section: string,
    subSection: string,
    tertiarySection?: string,
    quaternary?: string,
    itemId?: number,
    report?: string,
    voucherHolderId?: number,
    zipCode?: string,
    donorShieldId?: string,
    inviteToDS?: string,
}

interface IStateProps {
    user: IUser,
    center: ICenter,
    centerMetrics: ICenterMetrics,
    organType: OrganType
}

interface IDispatchProps {
    getCenter: (centerId: number) => Promise<ICenterResponse>,
    getCenterMetrics: (centerId: number) => Promise<ICenterMetricsResponse>,
    downloadFormsAndChecklist: (donorId: number, recipientId: number) => Promise<any>,
    downloadFile: (docType: number, lookupId: number) => Promise<any>,
    showLoading: (message: string) => Promise<void>,
    hideLoading: () => void
}

type TProps = IOwnProps & IStateProps & IDispatchProps;

interface IState {
    showMenu: boolean,
    ready: boolean
}

class CenterAdmin extends React.Component<TProps, IState> {
    readonly state: IState = {
        showMenu: true,
        ready: false
    }

    componentDidMount = () => {
        let { centerId } = this.props;

        if (centerId != this.props.user.centerId && this.props.user.centerId != 0) {
            window.location.href = "/not-authorized/" + this.props.user.centerId;
            return;
        }

        this.props.showLoading('Loading, one moment please...').then(() => {
            Promise.all([
                this.props.getCenter(centerId),
                this.props.getCenterMetrics(centerId)
            ]).then(() => {
                this.setState({ ready: true });
            }).finally(() => {
                this.props.hideLoading();
            });
        });
    }

    handleTabSelect = (key: string) => {
        window.location.href = `/center/${this.props.centerId}/${key}`;
    }

    handleMenu = () => {
        this.setState({
            showMenu: !this.state.showMenu
        });
    }

    handleMemberHomeClick = () => {
        window.location.href = `center/${this.props.centerId}`;
    }

    handleFormsAndCheckLists = (donorId: number, recipientId: number) => {
        this.props.downloadFormsAndChecklist(donorId, recipientId)
            .then((response: any) => {
                downloadFile(response.data, `forms-and-checklists-recipient-${recipientId}-donor-${donorId}.pdf`, 'application/pdf');
            });
    }

    handleGetFile = (docType: FileDocumentType, donorId: number) => {
        this.props.downloadFile(docType, donorId).then((response: any) => {
            downloadFile(response.data, `${FileDocumentTypeDescription[docType]}.pdf`, response.headers.contentType)
        }).catch((error: IBaseResponse) => {
            toast.error("Unable to download file at this time.  Please contact support.");
        })
    }

    render = () => {
        if (!this.state.ready)
            return null;

        const hasCenterBilling = this.props.center != null ? this.props.center.hasCenterBilling : false;
        const percentReviewed = this.props.centerMetrics != null ? this.props.centerMetrics.preselectsReviewed : 0;
        const pendingMatches = this.props.centerMetrics != null ? this.props.centerMetrics.pendingMatches : 0;
        const totalMatches = this.props.centerMetrics != null ? this.props.centerMetrics.totalMatches : 0;
        const averagePercentReviewedThisMonth = this.props.centerMetrics != null ? this.props.centerMetrics.averagePercentReviewedThisMonth : 0;
        const percentOfTitersIdentified = this.props.centerMetrics != null ? this.props.centerMetrics.percentOfTitersIdentified : 0;
        const declineRatio = this.props.center != null && this.props.center.declineRatio >= 0 ? this.props.center.declineRatio : -1;
        const percentOfDonorsSubTyped = this.props.centerMetrics != null ? this.props.centerMetrics.percentOfDonorsSubTyped : 0;
        const lowPercentWarning = (percentReviewed < 95 && pendingMatches > 0) || percentOfDonorsSubTyped < 95 || percentOfTitersIdentified < 95;
        const cprScore = this.props.center != null ? (this.props.center.cprScore * 100).toFixed(1) + '%' : 'unknown';
        const cprScoreValue = this.props.center != null ? this.props.center.cprScore : 0;
        const targetedChainEnd = this.props.center != null ? this.props.center.chainEndTarget : 'unknown';

        const nkrLiaisonName = this.props.center != null ? this.props.center.nkrLiaisonName : '';
        const nkrLiaisonFirstName = nkrLiaisonName.split(' ').length > 0 ? nkrLiaisonName.split(' ')[0] : '';
        const nkrLiaisonEmail = this.props.center != null ? this.props.center.nkrLiaisonEmail : '';
        const nkrLiaisonPhone = this.props.center != null ? this.props.center.nkrLiaisonPhone : '';

        const isMicrositeParticipant = this.props.center != null ? this.props.center.isMicrositeParticipant : false;
        const isFollowUpParticipant = this.props.center != null ? this.props.center.isFollowUpParticipant : false;
        const isInDonorShield = this.props.center != null ? this.props.center.isInDonorShield == 1 : false;

        const versionedFiles = this.props.center != null ? this.props.center.versionedFiles : [];
        const contract = versionedFiles.find(x => x.entityType == VersionedFileEntityType.CenterContract && parseFloat(x.documentsVersion.versionNumber) >= 4.2);

        const isStreamlinedContract = contract != null;

        const isNkr = this.props.organType == OrganType.Kidney
        const color = isNkr ? '#337ab7' : '#3B76B5';

        return (
            <>
                <div style={{ paddingBottom: 0 }}>
                    <div style={{ display: "flex" }}>
                        <div
                            className='no-print'
                            style={{
                                flexBasis: this.state.showMenu ? "7%" : "1%",
                                backgroundColor: color,
                                borderTop: "1px solid " + color,
                                // borderTopLeftRadius: 4,
                                // borderBottomLeftRadius: 4,
                                display: "flex",
                                flexDirection: "column",
                                justifyContent: "space-between"
                            }}>
                            <div className='no-print' style={{ flexBasis: "80%" }}>
                                {this.state.showMenu ?
                                    <NavVertical
                                        centerId={this.props.centerId}
                                        activeKey={this.props.section}
                                        hasCenterBilling={hasCenterBilling}
                                        onTabSelect={this.handleTabSelect}
                                    />
                                    : null}
                            </div>

                            <div className='no-print' style={{ paddingBottom: 10 }} onClick={this.handleMenu}>
                                <Glyphicon glyph={this.state.showMenu ? "triangle-left pull-right" : "triangle-right pull-right"} style={{ fontSize: 18, marginRight: this.state.showMenu ? -5 : -13, color: this.state.showMenu ? "white" : "rgb(51, 122, 183)" }} />
                            </div>

                        </div>

                        <div
                            className='print-full-width'
                            style={{
                                flexBasis: this.state.showMenu ? "93%" : "99%",
                                // borderColor: "#337ab7",
                                // borderWidth: "1px 1px 1px 0px",
                                // borderStyle: "solid",
                                // borderTopRightRadius: 4,
                                // borderBottomRightRadius: 4,
                                minHeight: "89vh",
                                padding: "15px 0px 15px 0px",
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'space-between'
                            }}>

                            {this.props.section.toUpperCase() == "ACTION-CENTER" && (
                                <ActionCenter
                                    centerId={this.props.centerId}
                                    section={this.props.subSection}
                                    subSection={this.props.tertiarySection}
                                    itemId={this.props.itemId}
                                    isAdmin={this.props.user.group.isSuper}
                                    lowPercentWarning={lowPercentWarning}
                                    zipCode={this.props.zipCode}
                                    donorShieldId={this.props.donorShieldId}
                                    inviteToDS={this.props.inviteToDS}
                                    organType={this.props.organType}
                                />
                            )}

                            {this.props.section.toUpperCase() == "KPD" && (
                                <Kpd
                                    centerId={this.props.centerId}
                                />
                            )}

                            {this.props.section.toUpperCase() == "BILLING" && (
                                <Billing
                                    centerId={this.props.centerId}
                                    centerAlias={this.props.center != null ? this.props.center.alias : ""}
                                    subSection={this.props.subSection}
                                    id={this.props.itemId}
                                />
                            )}

                            {this.props.section.toUpperCase() == 'DONORS' && (
                                <Donors
                                    history={this.props.history}
                                    location={this.props.location}
                                    match={this.props.match}
                                    center={this.props.center}
                                    isAdmin={false}
                                    isCenter={true}
                                    isAdminLogin={this.props.user.group.isSuper}
                                    subSection={this.props.subSection}
                                    tertiarySection={this.props.tertiarySection}
                                    donorId={this.props.itemId}
                                    regId={this.props.itemId}
                                    voucherHolderId={this.props.voucherHolderId}
                                />
                            )}

                            {this.props.section.toUpperCase() == 'RECIPIENTS' && (
                                <Recipients
                                    centerId={this.props.centerId != null ? this.props.centerId : 0}
                                    centerAlias={this.props.center != null ? this.props.center.alias : ''}
                                    hasRecipientShield={this.props.center != null ? this.props.center.hasRecipientShield : false}
                                    section={this.props.subSection}
                                    subSection={this.props.tertiarySection}
                                    recipientId={this.props.match.params.recipientId != null ? parseInt(this.props.match.params.recipientId) : 0}
                                    donorId={this.props.match.params.donorId != null ? parseInt(this.props.match.params.donorId) : 0}
                                    rexmId={this.props.itemId != null ? this.props.itemId : 0}
                                    voucherHolderId={this.props.itemId != null ? this.props.itemId : 0}
                                    matchHistoryId={this.props.itemId != null ? this.props.itemId : 0}
                                    isAdmin={false}
                                    isAdminLogin={this.props.user.group.isSuper}
                                    downloadFormsAndChecklist={this.handleFormsAndCheckLists}
                                    onGetFile={this.handleGetFile}
                                />
                            )}

                            {this.props.section.toUpperCase() == 'PROFILE' && (
                                <CenterProfileContainer
                                    isAdmin={false}
                                    isAdminLogin={this.props.user.group.isSuper}
                                    centerId={this.props.centerId}
                                    section={this.props.subSection}
                                    subSection={this.props.tertiarySection}
                                    contactId={this.props.itemId}
                                />
                            )}

                            {this.props.section.toUpperCase() == 'REPORTS' && (
                                <CenterReports
                                    primaryKey={this.props.subSection}
                                    secondaryKey={this.props.tertiarySection}
                                    tertiaryKey={this.props.quaternary}
                                    report={this.props.report}
                                    center={this.props.center}
                                />
                            )}

                            {this.props.section.toUpperCase() == 'MICROSITES' && (
                                <MicrositeContainer
                                    activeKey={this.props.subSection}
                                    center={this.props.center}
                                    centerId={this.props.centerId}
                                    isAdmin={false}
                                    itemId={this.props.itemId}
                                    onTabSelect={() => null}
                                />
                            )}

                            {this.props.section.toUpperCase() == 'INCIDENT-REPORT' && (
                                <div style={{ padding: '0px 10px' }}>
                                    <DonorsNav
                                        activeKey={'DONOR-INTAKE'}
                                        centerId={this.props.centerId}
                                        isMicrositeParticipant={isMicrositeParticipant}
                                        isAdmin={false}
                                        isFollowUpParticipant={isFollowUpParticipant}
                                        isInDonorShield={isInDonorShield}
                                        organType={this.props.organType}
                                        onTabSelect={() => null}
                                    />

                                    <IncidentReportDetails incidentReportId={this.props.itemId} />
                                </div>
                            )}

                            {this.props.section.toUpperCase() == 'DONOR-FOLLOW-UP' && (
                                <div style={{ padding: '0px 10px' }}>
                                    <DonorsNav
                                        activeKey={'DONOR-FOLLOW-UP'}
                                        centerId={this.props.centerId}
                                        isMicrositeParticipant={isMicrositeParticipant}
                                        isAdmin={false}
                                        isFollowUpParticipant={isFollowUpParticipant}
                                        isInDonorShield={isInDonorShield}
                                        organType={this.props.organType}
                                        onTabSelect={() => null}
                                    />

                                    <FollowUpContainer
                                        activeKey={this.props.subSection}
                                        subKey={this.props.tertiarySection}
                                        isAdmin={false}
                                        isAdminLogin={this.props.user.group.isSuper}
                                        centerId={this.props.centerId}
                                        itemId={this.props.itemId}
                                    />
                                </div>
                            )}

                            {this.props.section.toUpperCase() == 'DONOR' && (
                                <div style={{ padding: '0px 10px' }}>
                                    <Donor
                                        subSection={this.props.subSection}
                                        tertiarySection={this.props.tertiarySection}
                                        isAdmin={false}
                                        isAdminLogin={this.props.user.group.isSuper}
                                        centerId={this.props.centerId}
                                        donorId={this.props.itemId}
                                        isMicrositeParticipant={isMicrositeParticipant}
                                        isFollowUpParticipant={isFollowUpParticipant}
                                        isInDonorShield={isInDonorShield}
                                        organType={this.props.organType}
                                    />
                                </div>
                            )}

                            {this.props.section.toUpperCase() == "LEGAL" && (
                                <CenterLegalDocuments
                                    centerId={this.props.centerId}
                                    isAdmin={false}
                                    isAdminUser={this.props.user.group.isSuper}
                                    onTabClick={() => null} />
                            )}

                            {this.props.section.toUpperCase() == 'REGULATORY-COMPLIANCE' && (
                                <RegulatoryCompliance centerId={this.props.centerId} />
                            )}

                            {this.props.section.toUpperCase() == 'NETWORK-EMAIL-REPOSITORY' && (
                                <NetworkEmailSentList centerId={this.props.centerId} />
                            )}

                            {this.props.section.toUpperCase() == 'DONOR-SHIELD-DIRECT' && (
                                <div>NEW REPORT</div>
                            )}
                        </div>
                    </div>
                </div>

                <div className='no-print'>
                    <div className='action-center-footer'>
                        <div className='no-print text-center' style={{ padding: 5, backgroundColor: '#eee' }}>
                            Your liaison is {nkrLiaisonName}. You can contact {nkrLiaisonFirstName} via email (<a href={'mailto:' + nkrLiaisonEmail}>{nkrLiaisonEmail}</a>) or
                            via phone by calling {nkrLiaisonPhone}.
                            You can always contact NKR Logistics via email
                            (<a href='mailto:nkrlogistics@kidneyregistry.org'>nkrlogistics@kidneyregistry.org</a>) if you cannot reach {nkrLiaisonFirstName}.
                        </div>
                        <div className='top'>
                            <div style={{ padding: "0px 10px 5px 10px" }}>
                                {this.props.center != null && this.props.organType == OrganType.Kidney && (
                                    <>
                                        {isStreamlinedContract ?
                                            <a href={`https://www.kidneyregistry.com/membership-terms`} target="_blank">NKR Terms &amp; Conditions</a>
                                            :
                                            <a href={`https://www.kidneyregistry.com/membership-terms-legacy`} target="_blank">NKR Terms &amp; Conditions</a>
                                        }
                                    </>
                                )}
                                {this.props.center != null && this.props.organType == OrganType.Liver && (
                                    <a href="https://www.liverregistry.com/terms" target="_blank">NLR Terms &amp; Conditions</a>
                                )}
                            </div>
                            {isNkr && (
                                <>
                                    <div style={{ display: 'flex', alignItems: 'center', marginLeft: 40, marginRight: 20 }}>
                                        <div style={{ marginRight: 5 }}>% Pre-Selects Completed:</div>
                                        <div>
                                            <span className="badge" style={{ fontSize: 12, float: "none" }}>
                                                {totalMatches > 0 ? <>{percentReviewed}%</> : <>N/A</>}
                                            </span>
                                        </div>
                                    </div>

                                    <div style={{ display: 'flex', alignItems: 'center', marginRight: 20 }}>
                                        <div style={{ marginRight: 5 }}>30-day Average % Pre-Selected:</div>
                                        <div>
                                            {averagePercentReviewedThisMonth >= 0 ?
                                                <span className="badge" style={{ fontSize: 12, float: "none" }}>{averagePercentReviewedThisMonth.toFixed(1)}%</span>
                                                :
                                                <Badge>N/A</Badge>
                                            }
                                        </div>
                                    </div>

                                    <div style={{ display: 'flex', alignItems: 'center', marginRight: 20 }}>
                                        <div style={{ marginRight: 5 }}>% "O" &amp; "B" Recipient Titers Identified:</div>
                                        <div>
                                            {percentOfTitersIdentified >= 0 ?
                                                <span className="badge" style={{ fontSize: 12, float: "none" }}>{percentOfTitersIdentified}%</span>
                                                :
                                                <Badge>N/A</Badge>
                                            }
                                        </div>
                                    </div>

                                    <div style={{ display: 'flex', alignItems: 'center', marginRight: 20 }}>
                                        <div style={{ marginRight: 5 }}>Waitlist Offer Decline Ratio:</div>
                                        <div style={{ display: 'flex', alignItems: 'center' }}>
                                            {declineRatio >= 0 ?
                                                <>
                                                    <span className={"label " + (declineRatio > 30 ? 'label-danger' : 'label-success')} style={{ fontSize: 12, float: "none", borderRadius: 10 }}>{declineRatio}%</span>
                                                    <OverlayTrigger placement='top' overlay={<ChainEndDeclineRatioOverlay />}>
                                                        <span>
                                                            <Glyphicon glyph='info-sign' />
                                                        </span>
                                                    </OverlayTrigger>
                                                </>
                                                :
                                                <Badge>N/A</Badge>
                                            }
                                        </div>
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', marginRight: 20 }}>
                                        <div style={{ marginRight: 5 }}>CPR Score:</div>
                                        <div><span className="badge" style={{ fontSize: 12, float: "none" }}>{cprScoreValue >= 0 ? cprScore : 'N/A'}</span></div>
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center' }}>
                                        <div style={{ marginRight: 5 }}>Targeted Chain End:</div>
                                        <div><span className="badge" style={{ fontSize: 12, float: "none" }}>{(declineRatio >= 30 ? 0 : targetedChainEnd)}</span></div>
                                    </div>
                                </>
                            )}
                        </div>
                        {lowPercentWarning && (
                            <div className="text-center bottom" style={{ paddingTop: 2, paddingBottom: 2 }}>
                                If the above information is below 95% it could prevent your patients from receiving matches. Please ensure all information is reviewed and accurate.
                            </div>
                        )}
                    </div>
                </div>
            </>
        );
    }
}

const mapStateToProps = (state: IApplicationState): IStateProps => {
    return {
        user: state.app.user,
        center: state.app.center,
        centerMetrics: state.app.centerMetrics,
        organType: state.app.site.organType
    };
}

const mapDispatchToProps = (dispatch: Dispatch<IApplicationState>): IDispatchProps => {
    return {
        getCenter: (centerId) => {
            return dispatch(AppActionCreators.getCenter(centerId));
        },
        getCenterMetrics: (centerId) => {
            return dispatch(AppActionCreators.getCenterMetrics(centerId));
        },
        downloadFormsAndChecklist: (donorId, recipientId) => {
            return dispatch(FileActionCreators.getFormsAndChecklists(donorId, recipientId))
        },
        downloadFile: (docType, id) => {
            return dispatch(FileActionCreators.downloadFile(docType, id))
        },
        showLoading: (message) => {
            return dispatch(AppActionCreators.showLoadingAsync(message));
        },
        hideLoading: () => {
            return dispatch(AppActionCreators.hideLoading());
        }
    };
}

export default withRouter(connect<IStateProps, IDispatchProps, IOwnProps>(
    mapStateToProps,
    mapDispatchToProps)(CenterAdmin));